// src/pages/finance-portal/invoices/InvoiceDetail.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, Card, CardContent, Chip,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Alert, CircularProgress, Divider, Tooltip, Grid,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    MenuItem, Select, FormControl, InputLabel,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import BlockIcon from '@mui/icons-material/Block';
import DeleteIcon from '@mui/icons-material/Delete';
import PaymentIcon from '@mui/icons-material/Payment';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const STATUS_COLORS = {
    DRAFT: { bg: '#fef3c7', color: '#92400e' },
    SENT: { bg: '#dbeafe', color: '#1e40af' },
    VIEWED: { bg: '#e0e7ff', color: '#3730a3' },
    PARTIALLY_PAID: { bg: '#fef9c3', color: '#854d0e' },
    PAID: { bg: '#dcfce7', color: '#166534' },
    OVERDUE: { bg: '#fecaca', color: '#991b1b' },
    VOID: { bg: '#f3f4f6', color: '#6b7280' },
    CANCELLED: { bg: '#f3f4f6', color: '#6b7280' },
};

const PAYMENT_METHODS = [
    { value: 'CASH', label: 'Cash' },
    { value: 'CHECK', label: 'Check' },
    { value: 'CREDIT_CARD', label: 'Credit Card' },
    { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
    { value: 'ACH', label: 'ACH' },
    { value: 'WIRE', label: 'Wire Transfer' },
    { value: 'PAYPAL', label: 'PayPal' },
    { value: 'OTHER', label: 'Other' },
];

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

function formatDate(dateStr) {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getStatusLabel(status) {
    if (!status) return '';
    return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function getPaymentTermsLabel(terms) {
    const labels = {
        DUE_ON_RECEIPT: 'Due on Receipt',
        NET_15: 'Net 15',
        NET_30: 'Net 30',
        NET_60: 'Net 60',
        NET_90: 'Net 90',
    };
    return labels[terms] || terms || '—';
}

export default function InvoiceDetail() {
    const navigate = useNavigate();
    const { invoiceId } = useParams();

    const [invoice, setInvoice] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Action states
    const [sending, setSending] = useState(false);
    const [voiding, setVoiding] = useState(false);
    const [deletingInvoice, setDeletingInvoice] = useState(false);

    // Dialogs
    const [sendDialog, setSendDialog] = useState(false);
    const [voidDialog, setVoidDialog] = useState(false);
    const [deleteDialog, setDeleteDialog] = useState(false);

    // Payment dialog
    const [paymentDialog, setPaymentDialog] = useState(false);
    const [paymentSaving, setPaymentSaving] = useState(false);
    const [paymentForm, setPaymentForm] = useState({
        amount: '',
        payment_method: 'BANK_TRANSFER',
        payment_date: new Date().toISOString().split('T')[0],
        reference_number: '',
        notes: '',
    });

    const fetchInvoice = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get(`/api/finance/invoices/${invoiceId}`);
            if (res.data.success) {
                setInvoice(res.data.data);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load invoice');
        } finally {
            setLoading(false);
        }
    }, [invoiceId]);

    useEffect(() => { fetchInvoice(); }, [fetchInvoice]);

    const handleSend = async () => {
        setSending(true);
        setError(null);
        try {
            await axiosInstance.post(`/api/finance/invoices/${invoiceId}/send`);
            setSendDialog(false);
            setSuccess('Invoice sent successfully');
            fetchInvoice();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to send invoice');
        } finally {
            setSending(false);
        }
    };

    const handleVoid = async () => {
        setVoiding(true);
        setError(null);
        try {
            await axiosInstance.post(`/api/finance/invoices/${invoiceId}/void`);
            setVoidDialog(false);
            setSuccess('Invoice voided successfully');
            fetchInvoice();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to void invoice');
        } finally {
            setVoiding(false);
        }
    };

    const handleDelete = async () => {
        setDeletingInvoice(true);
        setError(null);
        try {
            await axiosInstance.delete(`/api/finance/invoices/${invoiceId}`);
            navigate('/finance-portal/invoices');
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete invoice');
        } finally {
            setDeletingInvoice(false);
        }
    };

    const handleRecordPayment = async () => {
        setPaymentSaving(true);
        setError(null);
        try {
            const payload = {
                invoice_id: parseInt(invoiceId, 10),
                amount: parseFloat(paymentForm.amount) || 0,
                payment_method: paymentForm.payment_method,
                payment_date: paymentForm.payment_date,
                reference_number: paymentForm.reference_number,
                notes: paymentForm.notes,
            };
            await axiosInstance.post('/api/finance/payments', payload);
            setPaymentDialog(false);
            setPaymentForm({
                amount: '',
                payment_method: 'BANK_TRANSFER',
                payment_date: new Date().toISOString().split('T')[0],
                reference_number: '',
                notes: '',
            });
            setSuccess('Payment recorded successfully');
            fetchInvoice();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to record payment');
        } finally {
            setPaymentSaving(false);
        }
    };

    const openPaymentDialog = () => {
        setPaymentForm({
            amount: invoice?.balance_due ? String(invoice.balance_due) : '',
            payment_method: 'BANK_TRANSFER',
            payment_date: new Date().toISOString().split('T')[0],
            reference_number: '',
            notes: '',
        });
        setPaymentDialog(true);
    };

    if (loading) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress sx={{ color: ACCENT }} />
            </Box>
        );
    }

    if (!invoice && !loading) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', p: 4 }}>
                <Alert severity="error" sx={{ borderRadius: '12px', fontFamily }}>
                    Invoice not found.
                    <Button onClick={() => navigate('/finance-portal/invoices')} sx={{ ml: 2, fontFamily, textTransform: 'none' }}>Back to Invoices</Button>
                </Alert>
            </Box>
        );
    }

    const statusStyle = STATUS_COLORS[invoice.status] || {};
    const customer = invoice.customer || {};
    const lineItems = invoice.line_items || [];
    const payments = invoice.payments || [];

    const canSend = invoice.status === 'DRAFT' || invoice.status === 'SENT' || invoice.status === 'VIEWED';
    const canVoid = invoice.status !== 'VOID' && invoice.status !== 'CANCELLED' && invoice.status !== 'PAID';
    const canEdit = invoice.status === 'DRAFT';
    const canDelete = invoice.status === 'DRAFT';
    const canRecordPayment = invoice.status !== 'VOID' && invoice.status !== 'CANCELLED' && invoice.status !== 'PAID' && (invoice.balance_due || 0) > 0;

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <IconButton onClick={() => navigate('/finance-portal/invoices')} sx={{ color: 'white', mr: 0.5 }}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <ReceiptLongIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>
                                    {invoice.invoice_number || 'Invoice'}
                                </Typography>
                                <Chip
                                    label={getStatusLabel(invoice.status)}
                                    size="small"
                                    sx={{
                                        fontFamily, fontSize: '0.75rem', fontWeight: 700, height: 26,
                                        bgcolor: 'rgba(255,255,255,0.25)', color: 'white',
                                    }}
                                />
                            </Box>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>
                                {customer.company_name || `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'No customer'}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {canRecordPayment && (
                            <Button
                                variant="contained"
                                startIcon={<PaymentIcon />}
                                onClick={openPaymentDialog}
                                sx={{
                                    fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                    bgcolor: 'white', color: ACCENT,
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                                }}
                            >
                                Record Payment
                            </Button>
                        )}
                        {canSend && (
                            <Button
                                variant="outlined"
                                startIcon={<SendIcon />}
                                onClick={() => setSendDialog(true)}
                                sx={{
                                    fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                    borderColor: 'rgba(255,255,255,0.5)', color: 'white',
                                    '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
                                }}
                            >
                                Send
                            </Button>
                        )}
                        {canEdit && (
                            <Button
                                variant="outlined"
                                startIcon={<EditIcon />}
                                onClick={() => navigate(`/finance-portal/invoices/${invoiceId}/edit`)}
                                sx={{
                                    fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                    borderColor: 'rgba(255,255,255,0.5)', color: 'white',
                                    '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
                                }}
                            >
                                Edit
                            </Button>
                        )}
                        {canVoid && (
                            <Tooltip title="Void Invoice">
                                <IconButton onClick={() => setVoidDialog(true)} sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'white' } }}>
                                    <BlockIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                        {canDelete && (
                            <Tooltip title="Delete Invoice">
                                <IconButton onClick={() => setDeleteDialog(true)} sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fecaca' } }}>
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1200, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}
                {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{success}</Alert>}

                {/* Status Chip (in body for reference) */}
                <Box sx={{ mb: 3 }}>
                    <Chip
                        label={getStatusLabel(invoice.status)}
                        sx={{
                            fontFamily, fontSize: '0.8rem', fontWeight: 700, height: 28,
                            bgcolor: statusStyle.bg, color: statusStyle.color,
                        }}
                    />
                </Box>

                {/* Customer & Invoice Info */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
                    {/* Customer Info */}
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <PersonIcon sx={{ fontSize: 20, color: '#94a3b8' }} />
                                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem', color: '#1e293b' }}>Customer</Typography>
                            </Box>
                            <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '1rem', color: '#1e293b', mb: 0.5 }}>
                                {customer.company_name || `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || '—'}
                            </Typography>
                            {customer.email && (
                                <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b', mb: 0.25 }}>{customer.email}</Typography>
                            )}
                            {customer.phone && (
                                <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b', mb: 0.25 }}>{customer.phone}</Typography>
                            )}
                            {(customer.billing_address_line1 || customer.billing_city) && (
                                <Box sx={{ mt: 1 }}>
                                    <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 600, mb: 0.5 }}>Billing Address</Typography>
                                    {customer.billing_address_line1 && <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>{customer.billing_address_line1}</Typography>}
                                    {customer.billing_address_line2 && <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>{customer.billing_address_line2}</Typography>}
                                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                        {[customer.billing_city, customer.billing_state, customer.billing_zip].filter(Boolean).join(', ')}
                                    </Typography>
                                    {customer.billing_country && <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>{customer.billing_country}</Typography>}
                                </Box>
                            )}
                        </CardContent>
                    </Card>

                    {/* Invoice Info */}
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <CalendarTodayIcon sx={{ fontSize: 20, color: '#94a3b8' }} />
                                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem', color: '#1e293b' }}>Invoice Info</Typography>
                            </Box>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                                <Box>
                                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 600 }}>Invoice Date</Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#1e293b', fontWeight: 500 }}>{formatDate(invoice.invoice_date)}</Typography>
                                </Box>
                                <Box>
                                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 600 }}>Due Date</Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#1e293b', fontWeight: 500 }}>{formatDate(invoice.due_date)}</Typography>
                                </Box>
                                <Box>
                                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 600 }}>Payment Terms</Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#1e293b', fontWeight: 500 }}>{getPaymentTermsLabel(invoice.payment_terms)}</Typography>
                                </Box>
                                <Box>
                                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 600 }}>PO Number</Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#1e293b', fontWeight: 500 }}>{invoice.po_number || '—'}</Typography>
                                </Box>
                                <Box>
                                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 600 }}>Currency</Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#1e293b', fontWeight: 500 }}>{invoice.currency || 'USD'}</Typography>
                                </Box>
                                <Box>
                                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 600 }}>Invoice #</Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#1e293b', fontWeight: 500 }}>{invoice.invoice_number || '—'}</Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>

                {/* Line Items */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 3 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b', mb: 2 }}>Line Items</Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        {['#', 'Description', 'Qty', 'Unit Price', 'Tax %', 'Discount %', 'Amount'].map(h => (
                                            <TableCell
                                                key={h}
                                                align={h === 'Amount' ? 'right' : 'left'}
                                                sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}
                                            >
                                                {h}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {lineItems.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                                                <Typography sx={{ fontFamily, color: '#94a3b8' }}>No line items</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : lineItems.map((li, idx) => {
                                        const base = (parseFloat(li.quantity) || 0) * (parseFloat(li.unit_price) || 0);
                                        const discPct = parseFloat(li.discount_percent) || 0;
                                        const amt = base - (base * discPct / 100);
                                        return (
                                            <TableRow key={li.id || idx} hover>
                                                <TableCell sx={{ fontFamily, fontSize: '0.8rem', color: '#94a3b8' }}>{idx + 1}</TableCell>
                                                <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#1e293b' }}>
                                                    {li.item_type && (
                                                        <Chip
                                                            label={li.item_type}
                                                            size="small"
                                                            sx={{ fontFamily, fontSize: '0.65rem', fontWeight: 600, height: 18, mr: 1, bgcolor: '#f1f5f9', color: '#475569' }}
                                                        />
                                                    )}
                                                    {li.description || '—'}
                                                </TableCell>
                                                <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>{li.quantity}</TableCell>
                                                <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>{formatCurrency(li.unit_price)}</TableCell>
                                                <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>{li.tax_rate ? `${li.tax_rate}%` : '—'}</TableCell>
                                                <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>{li.discount_percent ? `${li.discount_percent}%` : '—'}</TableCell>
                                                <TableCell align="right" sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>{formatCurrency(amt)}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>

                {/* Totals Summary */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
                    {/* Notes & Terms */}
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 3 }}>
                            {invoice.notes && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 600, mb: 0.5 }}>Notes</Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#475569', whiteSpace: 'pre-wrap' }}>{invoice.notes}</Typography>
                                </Box>
                            )}
                            {invoice.terms && (
                                <Box>
                                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 600, mb: 0.5 }}>Terms & Conditions</Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#475569', whiteSpace: 'pre-wrap' }}>{invoice.terms}</Typography>
                                </Box>
                            )}
                            {!invoice.notes && !invoice.terms && (
                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.9rem' }}>No notes or terms</Typography>
                            )}
                        </CardContent>
                    </Card>

                    {/* Totals */}
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <AttachMoneyIcon sx={{ fontSize: 20, color: '#94a3b8' }} />
                                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem', color: '#1e293b' }}>Summary</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#64748b' }}>Subtotal</Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.9rem', fontWeight: 600, color: '#1e293b' }}>{formatCurrency(invoice.subtotal)}</Typography>
                                </Box>
                                {(invoice.tax_amount || invoice.tax_rate) && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#64748b' }}>
                                            Tax{invoice.tax_rate ? ` (${invoice.tax_rate}%)` : ''}
                                        </Typography>
                                        <Typography sx={{ fontFamily, fontSize: '0.9rem', fontWeight: 600, color: '#1e293b' }}>{formatCurrency(invoice.tax_amount)}</Typography>
                                    </Box>
                                )}
                                {invoice.discount_amount > 0 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#64748b' }}>Discount</Typography>
                                        <Typography sx={{ fontFamily, fontSize: '0.9rem', fontWeight: 600, color: '#ef4444' }}>-{formatCurrency(invoice.discount_amount)}</Typography>
                                    </Box>
                                )}
                                {invoice.late_fee_amount > 0 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#64748b' }}>Late Fees</Typography>
                                        <Typography sx={{ fontFamily, fontSize: '0.9rem', fontWeight: 600, color: '#dc2626' }}>{formatCurrency(invoice.late_fee_amount)}</Typography>
                                    </Box>
                                )}
                                <Divider />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography sx={{ fontFamily, fontSize: '1rem', fontWeight: 700, color: '#1e293b' }}>Total</Typography>
                                    <Typography sx={{ fontFamily, fontSize: '1rem', fontWeight: 700, color: '#1e293b' }}>{formatCurrency(invoice.total)}</Typography>
                                </Box>
                                {(invoice.amount_paid || 0) > 0 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#64748b' }}>Amount Paid</Typography>
                                        <Typography sx={{ fontFamily, fontSize: '0.9rem', fontWeight: 600, color: ACCENT }}>-{formatCurrency(invoice.amount_paid)}</Typography>
                                    </Box>
                                )}
                                <Divider />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 0.5 }}>
                                    <Typography sx={{ fontFamily, fontSize: '1.1rem', fontWeight: 800, color: '#1e293b' }}>Balance Due</Typography>
                                    <Typography sx={{
                                        fontFamily, fontSize: '1.1rem', fontWeight: 800,
                                        color: (invoice.balance_due || 0) > 0 ? '#dc2626' : ACCENT,
                                    }}>
                                        {formatCurrency(invoice.balance_due)}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>

                {/* Payment History */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 3 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PaymentIcon sx={{ fontSize: 20, color: '#94a3b8' }} />
                                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem', color: '#1e293b' }}>Payment History</Typography>
                            </Box>
                            {canRecordPayment && (
                                <Button
                                    size="small"
                                    startIcon={<PaymentIcon />}
                                    onClick={openPaymentDialog}
                                    sx={{ fontFamily, textTransform: 'none', color: ACCENT, fontWeight: 600 }}
                                >
                                    Record Payment
                                </Button>
                            )}
                        </Box>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        {['Date', 'Method', 'Reference', 'Notes', 'Amount'].map(h => (
                                            <TableCell
                                                key={h}
                                                align={h === 'Amount' ? 'right' : 'left'}
                                                sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}
                                            >
                                                {h}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {payments.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.9rem' }}>No payments recorded</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : payments.map((pmt, idx) => (
                                        <TableRow key={pmt.id || idx} hover>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>{formatDate(pmt.payment_date)}</TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                {(pmt.payment_method || '').replace(/_/g, ' ')}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>{pmt.reference_number || '—'}</TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {pmt.notes || '—'}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: ACCENT }}>{formatCurrency(pmt.amount)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            </Box>

            {/* Send Confirmation Dialog */}
            <Dialog open={sendDialog} onClose={() => setSendDialog(false)} PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Send Invoice {invoice?.invoice_number}?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, color: '#64748b' }}>
                        This will send the invoice to {customer.email || 'the customer'} via email. The status will be updated to Sent.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setSendDialog(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSend}
                        disabled={sending}
                        startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}
                    >
                        {sending ? 'Sending...' : 'Send'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Void Confirmation Dialog */}
            <Dialog open={voidDialog} onClose={() => setVoidDialog(false)} PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Void Invoice {invoice?.invoice_number}?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, color: '#64748b' }}>
                        This will void the invoice. This action cannot be undone. Any outstanding balance will be written off.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setVoidDialog(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleVoid}
                        disabled={voiding}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}
                    >
                        {voiding ? 'Voiding...' : 'Void Invoice'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)} PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Delete Invoice {invoice?.invoice_number}?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, color: '#64748b' }}>
                        This will permanently delete this draft invoice and all its line items. This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDeleteDialog(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleDelete}
                        disabled={deletingInvoice}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}
                    >
                        {deletingInvoice ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Record Payment Dialog */}
            <Dialog open={paymentDialog} onClose={() => setPaymentDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Record Payment</DialogTitle>
                <DialogContent sx={{ pt: '16px !important' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="Amount"
                            type="number"
                            size="small"
                            required
                            value={paymentForm.amount}
                            onChange={e => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                            inputProps={{ min: 0, step: '0.01' }}
                            InputLabelProps={{ sx: { fontFamily } }}
                            InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                            helperText={`Balance due: ${formatCurrency(invoice?.balance_due)}`}
                            FormHelperTextProps={{ sx: { fontFamily } }}
                        />
                        <FormControl size="small">
                            <InputLabel sx={{ fontFamily }}>Payment Method</InputLabel>
                            <Select
                                value={paymentForm.payment_method}
                                onChange={e => setPaymentForm({ ...paymentForm, payment_method: e.target.value })}
                                label="Payment Method"
                                sx={{ fontFamily, borderRadius: '10px' }}
                            >
                                {PAYMENT_METHODS.map(pm => (
                                    <MenuItem key={pm.value} value={pm.value} sx={{ fontFamily }}>{pm.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Payment Date"
                            type="date"
                            size="small"
                            required
                            value={paymentForm.payment_date}
                            onChange={e => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                            InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                            InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        />
                        <TextField
                            label="Reference Number"
                            size="small"
                            value={paymentForm.reference_number}
                            onChange={e => setPaymentForm({ ...paymentForm, reference_number: e.target.value })}
                            placeholder="Check #, transaction ID, etc."
                            InputLabelProps={{ sx: { fontFamily } }}
                            InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        />
                        <TextField
                            label="Notes"
                            size="small"
                            multiline
                            rows={2}
                            value={paymentForm.notes}
                            onChange={e => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                            InputLabelProps={{ sx: { fontFamily } }}
                            InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button onClick={() => setPaymentDialog(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleRecordPayment}
                        disabled={paymentSaving || !paymentForm.amount || parseFloat(paymentForm.amount) <= 0}
                        startIcon={paymentSaving ? <CircularProgress size={16} color="inherit" /> : <PaymentIcon />}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}
                    >
                        {paymentSaving ? 'Saving...' : 'Record Payment'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
